import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const ParaguayPage = () => {
  const paraguayData = {
    name: 'Paraguay',
    region: 'Latin America',
    color: '#C85A54',
    metaTitle: 'Pharma & Nutraceutical Registration in Paraguay | DINAVISA | Suplementos Dietarios Rules',
    metaDescription: 'Learn how to register pharmaceuticals and nutraceuticals in Paraguay. DINAVISA\'s requirements, Decreto 3586/15 for suplementos dietarios, key documents and why Paraguay is an emerging chronic-care and wellness market in South America.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Paraguay',
      subtitle: 'An upper-middle-income market with growing obesity and diabetes burden, a defined framework for medicines and dietary supplements, and a proactive NRA (DINAVISA) recognised by PAHO—ideal for exporters with quality-driven portfolios.',
      cta: 'Start Your Paraguay Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~6.9–7.0 million (2024–2025 estimates)' },
      { label: 'Income Level', value: 'Upper-middle income (WHO / World Bank)' },
      { label: 'Health Expenditure', value: 'Current health expenditure ≈ 8% of GDP (2021)' },
      { label: 'Regulator', value: 'DINAVISA – Dirección Nacional de Vigilancia Sanitaria (MSPyBS)' },
      { label: 'Core Medicines Law', value: 'Law 1119/97 "De productos para la salud y otros" + implementing decrees' },
      { label: 'Supplements Framework', value: 'Decreto 3586/15 regulates suplementos dietarios manufacturing & registration' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Growing Retail Pharma & Regulatory Consolidation',
      stats: [
        'Retail pharmaceutical market estimated ~USD 380 million (2024–25) with projected 6.5–7% CAGR to 2030',
        'Market driven by generics and chronic-care therapies',
        'DINAVISA responsible for quality, safety, efficacy AND price regulation via Pricing Unit',
        'DINAVISA created by Law 1119/97, recognised by PAHO and integrated in EAMI network of 22 LatAm NRAs',
        'Decreto 3586/15 provides clear framework for suplementos dietarios: manufacturing, registration, quality control',
        'Recent DINAVISA resolutions fine-tune procedures for RSPA (supplement commercialization authorisation)'
      ],
      opportunities: [
        'Structured environment where solid dossiers and compliant labeling secure growing consumer base',
        'Clear legal bases for both medicines and dietary supplements',
        'Active NRA part of regional regulatory networks',
        'Growing chronic-care and wellness market with defined regulatory pathways'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Obesity, Diabetes & NCD Growth',
      subtitle: 'Paraguay faces high and rising obesity and diabetes prevalence, positioning it as a long-term market for cardio-metabolic therapies and wellness products.',
      categories: [
        {
          name: 'Obesity & Diabetes Burden',
          items: [
            'Obesity: 26.3% of adult women and 19.7% of adult men',
            'Diabetes: 9.6% of adult women and 9.2% of adult men',
            'Adult obesity prevalence roughly doubled over three decades (8.4% in 1990 → 18% in 2019)',
            'Paraguay ranks near top of obesity prevalence in South America (mid-30% of adults)',
            'World Obesity Federation data places Paraguay alongside Uruguay in regional rankings',
            'Increasing prevalence driving government efforts to improve access and affordability',
            'Rising demand for diabetes and weight-loss therapeutic solutions'
          ]
        },
        {
          name: 'Chronic Disease & Wellness Demand',
          items: [
            'Strong demand for antidiabetics, antihypertensives, lipid-lowering agents',
            'Growing market for cardio-renal therapies',
            'Increasing interest in anti-obesity and GLP-1-class drugs (including emerging generics)',
            'Pharma: Chronic-care combinations with strong quality data needed',
            'Nutra: Cardio-metabolic support supplements (omega-3, CoQ10, plant sterols)',
            'Nutra: Weight-management products with evidence-backed claims',
            'Women\'s health, bone/joint-health and general wellness formulations'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'DINAVISA – Dirección Nacional de Vigilancia Sanitaria (under MSPyBS)',
      description: 'DINAVISA has legal personality and administrative autonomy, created by Law 1119/97 "De productos para la salud y otros" to ensure quality, safety, efficacy and accessible prices of medicines and health products. Scope includes synthetic medicines, biologics, herbal/homeopathic products, nutritional products, cosmetics, medical devices. Dietary supplements governed by Decreto 3586/15, which establishes norms for fabrication, sanitary registration regime, quality control and commercialization.',
      pharma: {
        timeline: 'Registrations are valid for a defined period (commonly five years) and require timely renewal. Exact timelines vary based on product complexity, dossier quality and DINAVISA workload. Pricing Unit evaluation adds a step to ensure fair and reasonable pricing.',
        requirements: [
          'Foreign manufacturers must operate through local legal entity or representative (titular del Registro Sanitario)',
          'Law 1119/97 and implementing decrees plus DINAVISA resolutions govern registration',
          'CTD-structured dossiers (especially strong Module 3) increasingly used and accepted',
          'Application form to DINAVISA with proof of legal representative, power of attorney',
          'Fees and company documentation required',
          'Qualitative/quantitative formula and manufacturing process description',
          'Specifications & CoAs for APIs and finished product',
          'Stability data supporting claimed shelf-life',
          'Evidence of GMP for manufacturing site',
          'Bioequivalence or biowaiver justification for generics (where required)',
          'Non-clinical and clinical data for innovators or complex biologics',
          'Spanish labels and inserts with dosage, indications, warnings, storage',
          'Packaging mock-ups aligned with Paraguayan norms',
          'Pricing dossier for approval by DINAVISA Pricing Unit',
          'Certificado de Registro Sanitario de Medicamentos issued upon approval'
        ],
        insight: 'While Paraguay is not fully CTD-formal, international practice shows CTD-structured dossiers are increasingly accepted, particularly for multinational submissions. DINAVISA\'s Pricing Unit adds a unique layer - price dossiers must be evaluated and approved for new registrations and updates, ensuring fair and reasonable pricing for the market.'
      },
      nutra: {
        timeline: 'Classification-dependent. Products registered as health products under DINAVISA follow supplement registration pathway per Decreto 3586/15. Others without therapeutic claims may be handled by INAN as regular foods. Timeline depends on classification, documentation quality and claim substantiation.',
        requirements: [
          'Decreto 3586/15 is key regulation for dietary supplements (suplementos dietarios)',
          'Derives from Article 24 of Law 1119/97 for health products',
          'Sets rules for manufacturing, obtaining/renewing registration, quality and commercialization',
          'Supplements with therapeutic indications registered under DINAVISA',
          'Products without therapeutic claims may fall under INAN (food registration)',
          'Intended to complement diet with vitamins, minerals, herbal extracts, amino acids, bioactives',
          'Early classification analysis (food vs suplemento dietario vs medicine) essential',
          'Application with manufacturer details and full composition required',
          'Evidence on ingredient safety and justification of health claims',
          'Quality and GMP/food-safety documentation mandatory',
          'Stability data where shelf-life is claimed',
          'Spanish labels with mandatory warnings for risk groups (phenylketonuria, hypertension)',
          'Phenylalanine products: "El consumo de este producto no es conveniente en personas con fenilcetonuria"',
          'Nitric oxide ingredients: contraindicated in hypertensive individuals',
          'Proof of Free Sale/registration from country of origin often requested'
        ],
        insight: 'Decreto 3586/15 provides clear framework but classification is critical. Products with therapeutic indications must be registered as health products in DINAVISA; others fall under food authority INAN. Mandatory label warnings for specific ingredients (phenylalanine, nitric oxide-related) are strictly enforced. International evidence standards (EFSA/FDA-style) referenced for health claim substantiation.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with DINAVISA (CTD-Aligned Approach)',
      steps: [
        'Local Holder / MAH - Establish local legal entity or representative as titular del Registro Sanitario',
        'Legal Basis - Registration governed by Law 1119/97 and implementing decrees plus DINAVISA resolutions',
        'Dossier Format - CTD-structured dossiers (especially strong Module 3) increasingly accepted',
        'Application Form - Submit to DINAVISA with company documentation',
        'Legal Representative - Proof of authorization, power of attorney, fees',
        'Quality Dossier - Qualitative/quantitative formula, manufacturing process description',
        'Specifications & CoAs - For APIs and finished product',
        'Stability Studies - Supporting claimed shelf-life under appropriate conditions',
        'GMP Evidence - Certificate for manufacturing site from recognised authority',
        'Clinical/Non-Clinical - Bioequivalence for generics; full data for innovators/biologics',
        'Spanish Labelling - Labels, inserts, outer packaging with all mandatory information',
        'Packaging Mock-ups - Aligned with Paraguayan norms and requirements',
        'Pricing Dossier - For evaluation by DINAVISA Pricing Unit',
        'DINAVISA Assessment - Technical and pricing evaluation',
        'Certificado de Registro Sanitario - Issued upon approval, valid commonly for 5 years',
        'Renewal & Lifecycle - Timely renewal required; manage variations and extensions'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals & Suplementos Dietarios in Paraguay',
      steps: [
        'Legal Framework - Decreto 3586/15 governs dietary supplements derived from Article 24 of Law 1119/97',
        'Classification Check - Food vs suplemento dietario vs medicine (critical first step)',
        'Registration Path - Therapeutic indications → DINAVISA; no therapeutic claims → may be INAN',
        'Product Definition - Supplements complement diet with vitamins, minerals, herbal extracts, amino acids, bioactives',
        'Application - Submit to DINAVISA with manufacturer details',
        'Full Composition - Per unit and per serving disclosure',
        'Ingredient Safety Evidence - Justification of health properties claimed',
        'Quality Documentation - GMP or food-safety certifications',
        'Stability Data - Where shelf-life claims are made',
        'Spanish Labelling - Mandatory legends for risk groups required',
        'Phenylalanine Warning - "El consumo de este producto no es conveniente en personas con fenilcetonuria"',
        'Nitric Oxide Warning - Contraindicated in hypertensive individuals statement',
        'Free Sale Certificate - Proof of registration in country of origin often requested',
        'International Standards - Evidence aligned with EFSA/FDA-style requirements for health claims',
        'Registration Approval - Obtain sanitary registration before commercialization'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (Medicamentos)': [
        'Application form to DINAVISA',
        'Power of attorney and legal representative details',
        'Full product formula and specifications',
        'GMP certificate(s) and manufacturing authorisations',
        'Stability studies & CoAs',
        'Non-clinical / clinical modules (as relevant)',
        'Spanish labels, leaflets, outer pack artwork',
        'Packaging mock-ups aligned with Paraguayan norms',
        'Pricing dossier for approval by Pricing Unit',
        'Company documentation and fees'
      ],
      'Nutraceuticals / Suplementos Dietarios': [
        'Application with manufacturer details',
        'Full composition (per unit and per serving)',
        'Evidence on ingredient safety',
        'Justification of health claims with international standards',
        'Quality and GMP/food-safety documentation',
        'Stability data where shelf-life is claimed',
        'Spanish label drafts with mandatory legends',
        'Risk group warnings (phenylketonuria, hypertension as applicable)',
        'Proof of Free Sale or registration in country of origin',
        'Documentation meeting Decreto 3586/15 requirements'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Paraguay Belongs in Your LatAm Strategy',
      reasons: [
        'Growing Market - Retail pharma ~USD 380m with 6.5–7% projected CAGR driven by generics and chronic-care',
        'High Disease Burden - Rising obesity (26% women, 20% men) and diabetes (9%+) creates long-term demand',
        'Clear Legal Frameworks - Law 1119/97 for medicines and Decreto 3586/15 for supplements provide structured pathways',
        'Active Regulator - DINAVISA recognised by PAHO, part of EAMI network supporting regional convergence',
        'Quality Focus - DINAVISA emphasises quality, safety, efficacy with active post-marketing surveillance',
        'Price Transparency - Pricing Unit ensures fair and accessible pricing for new registrations',
        'Regional Integration - Experience in Paraguay supports expansion to Bolivia, Uruguay, northern Argentina within MERCOSUR'
      ],
      bestPortfolio: [
        'Pharma: Antidiabetics, antihypertensives, lipid-lowering agents; anti-obesity & weight-management drugs, GLP-1 generics/analogues (as frameworks evolve); chronic-care combinations with strong quality data',
        'Nutra: Cardio-metabolic supplements (omega-3, CoQ10, plant sterols, inositols, chromium); weight-management and lifestyle-health formulations with evidence-backed claims; women\'s health, bone/joint-health and general wellness products'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Paraguay',
      services: [
        'Portfolio–Disease Fit Analysis - Map SKUs against Paraguay obesity, diabetes and NCD burden to identify high-priority products',
        'CTD-Quality Dossier Development - Adapt existing CTD/ACTD dossiers to DINAVISA requirements under Law 1119/97; strengthen Module 3 quality section',
        'Pricing Dossier Preparation - Ensure pricing submissions meet DINAVISA Pricing Unit expectations for fair and reasonable pricing',
        'Suplementos Dietarios Strategy - Classify products correctly (supplement vs food vs medicine); review composition and claims per Decreto 3586/15',
        'Mandatory Warnings Compliance - Ensure labels include required risk group warnings (phenylalanine, nitric oxide, etc.)',
        'Local Partner Coordination - Identify suitable local MAH/distributor candidates, define documentation requirements, manage timeline expectations',
        'Lifecycle & Regional Expansion - Manage renewals, variations, line extensions; use Paraguay experience as reference for MERCOSUR markets'
      ]
    },
    
    faqs: [
      {
        question: 'Who is the national regulatory authority for medicines in Paraguay?',
        answer: 'The Dirección Nacional de Vigilancia Sanitaria (DINAVISA), under the Ministry of Public Health and Social Welfare (MSPyBS), is the National Regulatory Authority recognised by PAHO and part of the EAMI regional network of 22 Latin American NRAs.'
      },
      {
        question: 'What law governs medicines and health products?',
        answer: 'Law 1119/97 "De productos para la salud y otros" and its implementing regulations govern manufacturing, quality, distribution, import, export and registration of medicines and other health products in Paraguay.'
      },
      {
        question: 'How are dietary supplements regulated?',
        answer: 'Dietary supplements are regulated under Decreto 3586/15, which sets rules for manufacturing, sanitary registration, quality control and commercialization of productos denominados suplementos dietarios. Products with therapeutic indications are registered in DINAVISA; others without therapeutic claims may be handled by INAN as regular foods.'
      },
      {
        question: 'Is Paraguay a good market for metabolic-health products?',
        answer: 'Yes. Obesity affects over 20-26% of adults and diabetes around 9%, with prevalence rising. This drives strong demand for chronic-care medicines and weight-management solutions, both pharmaceutical and nutraceutical, making Paraguay an attractive market for metabolic-health portfolios.'
      },
      {
        question: 'How strict is DINAVISA on quality and price?',
        answer: 'DINAVISA emphasises quality, safety, efficacy and accessible prices. Its Pricing Unit evaluates price dossiers to ensure fairness and transparency. Quality control programmes and recent resolutions reinforce post-marketing surveillance, making thorough documentation essential.'
      }
    ]
  };

  return <CountryTemplate countryData={paraguayData} />;
};
